// Market sector types — Story #6837 (plan #6838) Stream 8a-1a.
// Mirrors the planned lambda/innovateflow-api/market/handler.py contract:
//   3b-1 literal routes  → taxonomy, reference, sectors?scope=own|portfolio
//   3b-2 per-project     → GET/PUT/POST /market/{project_id}…
// The backend ships in another lane, so the shapes below follow plan #6838
// exactly; fields the plan does not pin are marked "unpinned" and are
// optional so a later backend tightening cannot break a build.

/** Countries the Market tab can show, in display order. Mirrors
 * market/storage.py MARKET_COUNTRIES — the API returns 400 for anything else. */
export const MARKET_COUNTRIES = [
  'AUS', 'USA', 'GBR', 'DEU', 'FRA', 'JPN', 'KOR', 'CAN', 'NZL', 'WLD',
] as const

export type MarketCountry = (typeof MARKET_COUNTRIES)[number]

/** Display names. Mirrors storage.py COUNTRY_NAMES ('WLD' reads "World" on screen). */
export const COUNTRY_LABELS: Record<MarketCountry, string> = {
  AUS: 'Australia',
  USA: 'United States',
  GBR: 'United Kingdom',
  DEU: 'Germany',
  FRA: 'France',
  JPN: 'Japan',
  KOR: 'South Korea',
  CAN: 'Canada',
  NZL: 'New Zealand',
  WLD: 'World',
}

export type MarketRole = 'primary' | 'secondary'

/** 'completed' once at least one sector survives; 'insufficient_basis' when the
 * documents do not support a classification (ADR-022: never fabricated);
 * null = never analysed. */
export type MarketStatus = 'completed' | 'insufficient_basis'

// ── Taxonomy (GET /market/taxonomy) ─────────────────────────────────────────

export interface MarketTaxonomyClass { code: string; name: string }
export interface MarketTaxonomyDivision { code: string; name: string; classes: MarketTaxonomyClass[] }
export interface MarketTaxonomySection { code: string; name: string; divisions: MarketTaxonomyDivision[] }

export interface MarketTaxonomy {
  version: string
  sources: Array<{ url: string; fetched_at: string }>
  /** ISIC Rev.4 sections A–U. */
  sections: MarketTaxonomySection[]
  /** ANZSIC 2006 division letter → name. */
  anzsic_divisions: Record<string, string>
  /** ISIC section letter → ANZSIC division letter(s) shown alongside. */
  isic_section_to_anzsic_division: Record<string, string[]>
}

// ── Sectors ─────────────────────────────────────────────────────────────────

/** The D1 plaintext summary kept readable on the project row: section letter,
 * division number, role and a confidence number. Nothing else about a sector
 * is readable without the owner's consent. */
export interface MarketSectorSummary {
  section: string
  division: string
  role: MarketRole
  confidence: number
}

/** Full entry from the encrypted market_sectors_detail (owner organisation only). */
export interface MarketSectorDetail {
  /** 4-digit ISIC class code. */
  code: string
  division: string
  section: string
  label: string
  anzsic_divisions: string[]
  role: MarketRole
  source: 'auto' | 'owner'
  confidence: number
  /** CPC cross-check: agrees / disagrees / no_signal (unpinned object form). */
  cpc_check?: unknown
  /** Epoch seconds as served (a number on the live API). */
  set_at?: string | number
}

// ── Official statistics (reference table) ───────────────────────────────────

export interface MarketReferenceRow {
  country: string
  section: string
  value: number
  unit: string
  year: number
  /** worldbank | abs | oecd. */
  source: string
  level: string
  as_of: string
  /** Present on rows used to build range sources (unpinned for display). */
  source_url?: string
}

// ── PESTLE (shared, public reference data — ADR-011) ────────────────────────

export const PESTLE_CELLS = [
  'political', 'economic', 'social', 'technological', 'legal', 'environmental',
] as const

export type PestleCell = (typeof PESTLE_CELLS)[number]
export type PestleSignal = 'supportive' | 'mixed' | 'tightening'

export interface PestleItem {
  text: string
  signal: PestleSignal
  /** Index into MarketPestle.sources; null when the model cited nothing (live API, no web snippets). */
  source_idx: number | null
}

export interface MarketSource {
  /** https only. */
  url: string
  title?: string
}

export interface MarketPestle {
  cells: Record<PestleCell, PestleItem[]>
  generated_at: string
  grounding: 'web' | 'official-only'
  sources: MarketSource[]
}

// ── SWOT (tenant-scoped, encrypted) ─────────────────────────────────────────

export const SWOT_QUADRANTS = ['strengths', 'weaknesses', 'opportunities', 'threats'] as const
export type SwotQuadrant = (typeof SWOT_QUADRANTS)[number]

/** A quadrant entry. Model items arrive as text; owner overlay items merged on
 * read carry who/when (unpinned: the merged form is finalised in 7b). */
export type MarketSwotEntry = string | { text: string; by?: string; at?: string; source?: string }

export interface MarketSwot extends Record<SwotQuadrant, MarketSwotEntry[]> {
  run_at: string
  primary_code: string
  /** ISO3 the SWOT was generated for — may differ from the selected country. */
  country: string
  /** True when the primary sector changed since this was generated. */
  stale: boolean
}

// ── Modelled market range (tenant-scoped, encrypted) ────────────────────────

export interface MarketRangeAssumption {
  key: string
  label: string
  value: number
  editable: boolean
}

export interface MarketRange {
  /** [low, high] in `currency`. */
  tam: [number, number]
  sam: [number, number]
  som: [number, number]
  currency: string
  assumptions: MarketRangeAssumption[]
  confidence: number
  /** Built server-side from the reference rows, never from model text. */
  sources: MarketSource[]
  generated_at: string
  primary_code: string
  /** ISO3 the range was generated for — may differ from the selected country. */
  country: string
  /** The owner's saved assumption overrides (PUT range-overrides); {} when none. */
  overrides?: MarketRangeOverrides
  stale: boolean
}

// ── GET /market/{project_id}?country=AUS ────────────────────────────────────

export interface MarketProject {
  sectors: MarketSectorSummary[]
  /** Empty unless the caller's organisation can decrypt the project. */
  detail: MarketSectorDetail[]
  /** 4-digit codes the owner removed (the owner's organisation only). Optional: the
   * API adds it in Task #6871; until then the panel tracks removals made this session. */
  removed?: string[]
  swot: MarketSwot | null
  range: MarketRange | null
  /** PESTLE for the SELECTED country only; null until generated for it. */
  pestle: MarketPestle | null
  /** Primary section's statistics across ALL MARKET_COUNTRIES. */
  reference: MarketReferenceRow[]
  status: MarketStatus | null
  /** A run lease is still live. */
  running: boolean
  /** Edit permission AND decryptable AND not an excluded-folder aggregator. */
  can_run: boolean
}

// ── Portfolio / own lists ───────────────────────────────────────────────────

export interface MarketOwnProject {
  sectors: MarketSectorSummary[]
  status: MarketStatus | null
}

/** GET /market/sectors?scope=own&country=AUS */
export interface MarketOwnSectors {
  projects: Record<string, MarketOwnProject>
  /** One reference value per section for the requested country. */
  reference: Record<string, MarketReferenceRow>
  /** More than 500 projects: the list was capped. */
  truncated?: boolean
}

export interface MarketPortfolioProject extends MarketOwnProject {
  assessment: number | null
  som_mid: number | null
  som_currency?: string | null
  /** When the assessment was made (set only when there is a score). */
  assessment_at?: string | null
  /** When the market range behind som_mid was generated (set only with som_mid). */
  som_at?: string | null
}

export type MarketPortfolioReferenceValue = Omit<MarketReferenceRow, 'section' | 'country'>

/** GET /market/sectors?scope=portfolio */
export interface MarketPortfolioSectors {
  projects: Record<string, MarketPortfolioProject>
  reference: Record<string, { AUS?: MarketPortfolioReferenceValue; WLD?: MarketPortfolioReferenceValue }>
  /** More than 500 projects: the list was capped. */
  truncated: boolean
}

/** GET /market/reference/{section}?country=AUS (unpinned envelope: mirrors the
 * project GET's `reference` key — verify when 3b-1 lands). */
export interface MarketReferenceResponse {
  section?: string
  country?: string
  reference: MarketReferenceRow[]
}

// ── Writes ──────────────────────────────────────────────────────────────────

/** PUT /market/{id}/sectors — ≤5 entries, exactly one primary. */
export interface MarketSectorsUpdate {
  sectors: Array<{ code: string; role: MarketRole }>
  /** 4-digit codes the owner removed (≤50); auto will not re-add them. Omit to leave the
   * stored list untouched; when sent it REPLACES the list. */
  removed?: string[]
}

export type MarketAnalyseStep = 'pestle' | 'swot' | 'range'

/** POST /market/{id}/analyse body — `step` is required, one model call each. */
export interface MarketAnalyseRequest {
  step: MarketAnalyseStep
  country: string
  force?: boolean
}

export interface MarketAnalyseResult {
  step: MarketAnalyseStep
  status: 'done' | 'insufficient_basis' | 'timeout' | 'error'
}

export interface MarketClassifyResult {
  status: 'completed' | 'insufficient_basis' | 'timeout' | 'error'
}

/** 429 body from the spend controls. */
export interface MarketRateLimited {
  error: 'rate_limited'
  kind: string
  retry_after: number
}

export interface SwotOverlayItem { quadrant: SwotQuadrant; text: string }

/** PUT /market/{id}/range-overrides — keys must be editable assumptions. */
export type MarketRangeOverrides = Record<string, number>

// ── Shared by the three panels (pinned by plan #6838) ───────────────────────

export type MarketPanelProps = {
  projectId: string
  market: MarketProject
  country: string
  onCountryChange: (c: string) => void
  canRun: boolean
}
